import { useMemo } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { cn } from "@asm/ui";
import {
  AlertTriangle,
  Grid3X3,
  Route,
  Clock,
  Network,
  Layers,
  ShieldOff,
} from "lucide-react";
import { FEATURES, type FeatureFlag } from "@/lib/features";

// All asset views with optional feature flags
const allAssetViews = [
  {
    name: "Inventory",
    href: "/assets",
    icon: Layers,
    description: "All discovered assets",
    // No feature flag - always visible
  },
  {
    name: "Risks",
    href: "/assets/risks",
    icon: AlertTriangle,
    description: "Security risks needing attention",
    feature: "riskDashboard" as FeatureFlag,
  },
  {
    name: "Matrix",
    href: "/assets/matrix",
    icon: Grid3X3,
    description: "Service exposure heatmap",
    feature: "serviceMatrix" as FeatureFlag,
  },
  {
    name: "Paths",
    href: "/assets/paths",
    icon: Route,
    description: "Potential attack chains",
    feature: "attackPaths" as FeatureFlag,
  },
  {
    name: "Timeline",
    href: "/assets/timeline",
    icon: Clock,
    description: "Asset changes over time",
    feature: "timeline" as FeatureFlag,
  },
  {
    name: "Graph",
    href: "/assets/graph",
    icon: Network,
    description: "Topology visualization",
    feature: "graphVisualization" as FeatureFlag,
  },
  {
    name: "Exceptions",
    href: "/assets/exceptions",
    icon: ShieldOff,
    description: "Assets not owned by you",
  },
];

export default function AssetsLayout() {
  const location = useLocation();

  // Filter views based on feature flags
  const assetViews = useMemo(() =>
    allAssetViews.filter(view => !view.feature || FEATURES[view.feature]),
    []
  );

  // Determine which tab is active based on pathname
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === "/assets" || path === "/assets/") return "/assets";
    // Match specific routes
    for (const view of assetViews) {
      if (view.href !== "/assets" && path.startsWith(view.href)) {
        return view.href;
      }
    }
    // Default to inventory for asset detail pages like /assets/:id
    if (path.startsWith("/assets/") && !assetViews.some(v => path.startsWith(v.href) && v.href !== "/assets")) {
      return "/assets";
    }
    return "/assets";
  };

  const activeTab = getActiveTab();

  return (
    <div className="space-y-6">
      {/* Header with navigation tabs */}
      <div className="border-b">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Asset Intelligence</h1>
            <p className="text-muted-foreground">
              Comprehensive view of your attack surface
            </p>
          </div>
        </div>

        {/* Tab navigation */}
        <nav className="-mb-px flex space-x-1 overflow-x-auto" aria-label="Asset views">
          {assetViews.map((view) => {
            const isActive = activeTab === view.href;
            const Icon = view.icon;

            return (
              <Link
                key={view.href}
                to={view.href}
                className={cn(
                  "group inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
                )}
              >
                <Icon
                  className={cn(
                    "h-4 w-4 transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground group-hover:text-foreground"
                  )}
                />
                {view.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Content area - will render child routes */}
      <Outlet />
    </div>
  );
}
